import React from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Pagination,
    PaginationItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

type Column<T> = {
    header: string;
    field?: keyof T;
    width?: string | number;
    align?: 'left' | 'right' | 'center';
    render?: (row: T) => React.ReactNode;
};

type GenericTablePaginationProps<T> = {
    data: T[];
    columns: Column<T>[];
    page: number;
    totalItems: number;
    totalPages: number;
    pageSize: number;
    onPageChange: (newPage: number) => void;
};

export function GenericTablePagination<T>({
    data,
    columns,
    page,
    totalItems,
    totalPages,
    pageSize,
    onPageChange,
}: GenericTablePaginationProps<T>) {
    const {t} = useTranslation();
    return (
        <Box sx={{ width: "100%", height: "100%", border: "1px solid #2D2D2D33", borderRadius: "6px", display: "grid", alignContent: "space-between" }}>
            <TableContainer sx={{ boxShadow: "none", borderRadius: "none", overflowY: 'auto' }} component={Paper}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col, index) => (
                                <TableCell key={index} align={col.align} style={{ width: col.width }}>
                                    {col.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {columns.map((col, colIndex) => (
                                    <TableCell key={colIndex} align={col.align}>
                                        {col.render ? col.render(row) : row[col.field as keyof T]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Rodapé */}
            <Box display="flex" justifyContent="center" position="relative" borderTop="1px solid #2D2D2D33" mt={2} px={1}>
                <Typography sx={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", marginLeft: "10px" }} variant="body2">
                    {`${t("GenericTablePagination.showing")} ${data.length} ${t("GenericTablePagination.itemsOutOf")} ${totalItems} ${t("GenericTablePagination.totalItems")}`}
                </Typography>
                <Pagination
                size="small"
                    sx={{
                        '& .Mui-selected': {
                            backgroundColor: '#22638D !important',
                            color: '#FFFFFF !important',
                        },
                        padding: "5px"
                    }}
                    shape="rounded"
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => (totalPages > 1) ? onPageChange(value) : null}
                />
            </Box>
        </Box>
    );
}
